import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/apiClient';

const parseNum=v=>{const n=Number(String(v??'').replace(',','.'));return Number.isFinite(n)?n:0};
const formatQty=v=>new Intl.NumberFormat('tr-TR',{maximumFractionDigits:2}).format(parseNum(v));
const descendants=(cats,id)=>{const out=new Set([id]);let changed=true;while(changed){changed=false;cats.forEach(c=>{if(c.parent_id&&out.has(c.parent_id)&&!out.has(c.id)){out.add(c.id);changed=true}})}return out};

function clearVisibleSelection(){
  document.querySelectorAll('table tbody input[type="checkbox"]:checked').forEach(box=>{
    try{box.click()}catch(e){}
  });
}

function categoryMetricKey(name,direct,total){
  return `${String(name||'').trim()}|${Number(direct||0)}|${Number(total||0)}`;
}

function decorateCategoryStockTotals(metrics,allStock,allCards){
  const section=[...document.querySelectorAll('section')].find(node=>String(node.textContent||'').includes('Kategori Ağacı'));
  if(!section)return;

  section.querySelectorAll('button').forEach(button=>{
    const nameNode=button.querySelector('span.font-medium.truncate');
    const meta=button.querySelector('span.ml-auto.text-xs.text-muted-foreground');
    if(!meta)return;

    if(!nameNode&&String(button.textContent||'').trim().startsWith('Tümü')){
      meta.textContent=`${Number(allCards||0)} kart · ${formatQty(allStock)} adet stok`;
      return;
    }
    if(!nameNode)return;

    const text=String(meta.textContent||'').trim();
    const match=text.match(/(\d+)\s+doğrudan\s*·\s*(\d+)\s+(?:toplam|kart)/i);
    if(!match)return;
    const key=categoryMetricKey(nameNode.textContent,Number(match[1]),Number(match[2]));
    const metric=metrics.get(key);
    if(!metric)return;
    meta.textContent=`${metric.direct} doğrudan · ${metric.total} kart · ${formatQty(metric.stock)} adet stok`;
  });
}

export default function WarehouseStockBehaviorBridge(){
  const {user}=useAuth();
  const location=useLocation();

  // Stok kartı alış fiyatı backend'e giderken yalnızca Super Admin tarafından
  // değiştirilebilsin. Bu interceptor DOM izleme yapmaz ve mobil performansı etkilemez.
  useEffect(()=>{
    const requestId=api.interceptors.request.use(config=>{
      try{
        const url=String(config.url||'');
        const method=String(config.method||'get').toLowerCase();
        if(method==='put'&&/^\/warehouse\/items\/[^/]+$/.test(url)&&config.data&&typeof config.data==='object'){
          const data={...config.data};
          if(user?.role!=='super_admin'){
            delete data.purchase_price;
            delete data.purchase_price_net;
            delete data.purchase_price_gross;
            delete data.purchase_vat_amount;
            delete data.purchase_currency;
          }else if('purchase_price' in data){
            const net=Math.max(parseNum(data.purchase_price),0);
            const vat=Math.max(parseNum(data.vat_rate),0);
            data.purchase_price_gross=net*(1+vat/100);
            delete data.purchase_price_net;
            delete data.purchase_vat_amount;
          }
          config.data=data;
        }
      }catch(e){}
      return config;
    });
    return()=>api.interceptors.request.eject(requestId);
  },[user?.role]);

  // Yalnız stok ekranındaki gerçek kategori filtresi / kategori ağacı değiştiğinde
  // önceki kategoriden kalan checkbox seçimlerini temizle. Toplu Kategori Değiştir
  // modalındaki "Hedef Kategori / Alt Kategori" alanı bu kurala dahil değildir;
  // hedef kategori seçilirken mevcut toplu seçim korunmalıdır.
  useEffect(()=>{
    if(location.pathname.replace(/\/+$/,'')!=='/depo/stok')return undefined;

    const onClick=event=>{
      const button=event.target?.closest?.('button');
      if(!button)return;
      const section=button.closest('section');
      if(!section||!String(section.textContent||'').includes('Kategori Ağacı'))return;
      const text=String(button.textContent||'').trim();
      if(text==='Tümü'||text.includes('doğrudan'))clearVisibleSelection();
    };

    const onChange=event=>{
      const select=event.target;
      if(select?.tagName!=='SELECT')return;
      const label=select.closest('label');
      if(!label)return;
      const caption=String(label.querySelector(':scope > span')?.textContent||'').trim();
      if(caption==='Kategori / Alt Kategori')clearVisibleSelection();
    };

    document.addEventListener('click',onClick,true);
    document.addEventListener('change',onChange,true);
    return()=>{
      document.removeEventListener('click',onClick,true);
      document.removeEventListener('change',onChange,true);
    };
  },[location.pathname]);

  // Kategori ağacındaki stok kartı sayılarının yanına, alt kategoriler dahil gerçek
  // stock_qty toplamını ekle. MutationObserver kullanılmaz; iOS/mobil performansını
  // korumak için yalnız sayfa açılışında, kategori tıklamalarında ve stok/kategori
  // mutation'larından sonra yeniden hesaplanır.
  useEffect(()=>{
    if(location.pathname.replace(/\/+$/,'')!=='/depo/stok')return undefined;
    let disposed=false;
    let metrics=new Map();
    let allStock=0;
    let allCards=0;
    const timers=new Set();

    const scheduleDecorate=(delay=0)=>{
      const id=window.setTimeout(()=>{
        timers.delete(id);
        if(!disposed)decorateCategoryStockTotals(metrics,allStock,allCards);
      },delay);
      timers.add(id);
    };

    const refresh=async()=>{
      try{
        const [itemsResponse,categoriesResponse]=await Promise.all([
          api.get('/warehouse/items'),
          api.get('/warehouse/categories'),
        ]);
        if(disposed)return;
        const items=itemsResponse.data?.items||[];
        const cats=categoriesResponse.data?.items||[];
        const next=new Map();
        cats.forEach(c=>{
          const ids=descendants(cats,c.id);
          const related=items.filter(x=>ids.has(x.category_id));
          const direct=items.filter(x=>x.category_id===c.id).length;
          const total=related.length;
          const stock=related.reduce((sum,x)=>sum+parseNum(x.stock_qty),0);
          next.set(categoryMetricKey(c.name,direct,total),{direct,total,stock});
        });
        metrics=next;
        allCards=items.length;
        allStock=items.reduce((sum,x)=>sum+parseNum(x.stock_qty),0);
        scheduleDecorate(0);
        scheduleDecorate(80);
        scheduleDecorate(250);
      }catch(e){}
    };

    const onTreeClick=event=>{
      const section=event.target?.closest?.('section');
      if(!section||!String(section.textContent||'').includes('Kategori Ağacı'))return;
      scheduleDecorate(30);
      scheduleDecorate(120);
    };

    const responseId=api.interceptors.response.use(response=>{
      try{
        const method=String(response?.config?.method||'get').toLowerCase();
        const url=String(response?.config?.url||'');
        if(['post','put','patch','delete'].includes(method)&&/^\/warehouse\/(items|categories)(\/|$)/.test(url)){
          scheduleDecorate(100);
          window.setTimeout(refresh,180);
        }
      }catch(e){}
      return response;
    },error=>Promise.reject(error));

    document.addEventListener('click',onTreeClick,true);
    refresh();
    return()=>{
      disposed=true;
      document.removeEventListener('click',onTreeClick,true);
      api.interceptors.response.eject(responseId);
      timers.forEach(id=>window.clearTimeout(id));
      timers.clear();
    };
  },[location.pathname]);

  // Eski sürümde barkod modalını document.body seviyesinde MutationObserver ile
  // sürekli tarayıp iframe/button DOM'unu değiştiriyorduk. iOS Safari/Chrome'daki
  // depo kilitlenmesinin ana risk noktası buydu. Barkod yazdırma artık WarehouseStock
  // bileşeninin kendi React akışında yönetilecek; burada hiçbir global observer yok.
  return null;
}
